import React, { useState, useMemo } from 'react';
import { 
    Autocomplete, TextField, Box, Avatar, Typography, 
    InputAdornment, CircularProgress, Button 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import debounce from 'lodash.debounce';

// Importamos tu controlador existente y la URL base de imágenes

import { imagesURL } from '../../apiRoutes/apiBase'; 
import { searchEntities } from '../../controllers/EntitiesController';
import { SearchPerson } from '../../controllers/company/crm/CustomersController';

export default function InlineEntityFinder({ 
    label = "Buscar cliente...", 
    onSelectEntity, 
    onAddNew // Función que ejecutaremos si el cliente no existe
}) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Búsqueda en vivo (debounce espera 500ms después de que dejas de escribir para no saturar el server)
    const handleSearch = useMemo(() => debounce(async (query) => {
        if (query.length < 3) {
            setOptions([]);
            return;
        }

        setLoading(true);
        try {
            let response = await SearchPerson(query);
            // Aseguramos que siempre sea un array para que no explote el Autocomplete
            const results = response?.data || [];
            setOptions(Array.isArray(results) ? results : []);
        } catch (error) {
            console.error("Error buscando entidades:", error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, 500), []);

    return (
        <Autocomplete
            options={options}
            getOptionLabel={(option) => `${option.name} ${option.last_name || ''}`}
            filterOptions={(x) => x} // Apagamos el filtro local, el backend hace el trabajo
            onInputChange={(event, newInputValue) => handleSearch(newInputValue)}
            onChange={(event, newValue) => {
                onSelectEntity(newValue); // Enviamos el objeto completo al componente padre
            }}
            loading={loading}
            loadingText="Buscando..."
            noOptionsText={
                <Box textAlign="center" py={1}>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        No se encontraron resultados
                    </Typography>
                    {onAddNew && (
                        <Button 
                            variant="outlined" 
                            size="small" 
                            startIcon={<FontAwesomeIcon icon={faUserPlus} />}
                            onMouseDown={(e) => {
                                e.preventDefault(); // Evita que se cierre el menú antes del clic
                                onAddNew();
                            }}
                            sx={{ borderRadius: 2 }}
                        >
                            Crear nuevo
                        </Button>
                    )}
                </Box>
            }
            renderInput={(params) => (
                <TextField 
                    {...params} 
                    label={label}
                    placeholder="Escribe nombre, cédula o email..." 
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <FontAwesomeIcon icon={faSearch} color="#ccc"/>
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    <Avatar 
                        alt={`${option.name}`} 
                        src={`${imagesURL}/users/${option.profile_image}`} 
                        sx={{ width: 40, height: 40, mr: 2 }} 
                    />
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            {option.name} {option.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {option.email}
                        </Typography>
                        {option.document_type && option.document_number && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {option.document_type}-{option.document_number}
                            </Typography>
                        )}
                    </Box>
                </li>
            )}
        />
    );
}